import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/admin/settings'); setS(data); }
    catch (e) { console.error('Failed to load settings:', e); toast.error('Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!s) return <div className="text-muted-foreground">Loading…</div>;

  const update = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/settings', {
        horse_price_yearly: Number(s.horse_price_yearly),
        pony_price_yearly: Number(s.pony_price_yearly),
        currency: s.currency,
        benefits: s.benefits,
        faq: s.faq,
        tags: s.tags,
        contact_email: s.contact_email,
      });
      toast.success('Settings saved.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const addBenefit = () => update('benefits', [...(s.benefits || []), '']);
  const removeBenefit = (i) => update('benefits', s.benefits.filter((_, idx) => idx !== i));
  const setBenefit = (i, v) => update('benefits', s.benefits.map((b, idx) => idx === i ? v : b));

  const addFaq = () => update('faq', [...(s.faq || []), { q: '', a: '' }]);
  const removeFaq = (i) => update('faq', s.faq.filter((_, idx) => idx !== i));
  const setFaq = (i, k, v) => update('faq', s.faq.map((f, idx) => idx === i ? { ...f, [k]: v } : f));

  const addTag = () => update('tags', [...(s.tags || []), '']);
  const removeTag = (i) => update('tags', s.tags.filter((_, idx) => idx !== i));
  const setTag = (i, v) => update('tags', s.tags.map((t, idx) => idx === i ? v : t));

  return (
    <div data-testid={ADMIN.settingsPage} className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase  text-muted-foreground">Configure</p>
          <h1 className="font-extrabold text-3xl mt-1">Settings</h1>
        </div>
        <Button data-testid={ADMIN.settingsSave} onClick={save} disabled={saving} className="rounded-xl bg-[#1E3A2F]">
          {saving ? 'Saving…' : 'Save all'}
        </Button>
      </div>

      <Tabs defaultValue="pricing">
        <TabsList className="bg-[hsl(var(--secondary))] flex-wrap h-auto">
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="benefits">Benefits</TabsTrigger>
          <TabsTrigger value="faq">FAQ</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
        </TabsList>

        <TabsContent value="pricing" className="mt-4">
          <Card className="p-6 rounded-2xl border-border space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>Horse sponsorship (yearly)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">₹</span>
                  <Input type="number" value={s.horse_price_yearly || 0} onChange={(e) => update('horse_price_yearly', e.target.value)} />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label>Pony sponsorship (yearly)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">₹</span>
                  <Input type="number" value={s.pony_price_yearly || 0} onChange={(e) => update('pony_price_yearly', e.target.value)} />
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="benefits" className="mt-4">
          <Card className="p-6 rounded-2xl border-border space-y-3">
            {/* Editable list of primitives — index keys are intentional here to
                preserve input focus while the user types (using content as key
                would remount the input on every keystroke). */}
            {(s.benefits || []).map((b, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`benefit-${i}`} className="flex items-start gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#DEEFE4] text-[#1E3A2F] font-extrabold text-sm flex-none mt-1">{i + 1}</span>
                <Textarea rows={2} value={b} onChange={(e) => setBenefit(i, e.target.value)} className="flex-1" />
                <Button variant="ghost" size="icon" onClick={() => removeBenefit(i)} className="text-destructive mt-1"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" onClick={addBenefit} className="rounded-lg"><Plus className="h-4 w-4 mr-1.5" />Add benefit</Button>
          </Card>
        </TabsContent>

        <TabsContent value="faq" className="mt-4">
          <Card className="p-6 rounded-2xl border-border space-y-4">
            {(s.faq || []).map((f, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`faq-${i}`} className="space-y-2 border-b border-border pb-4 last:border-0">
                <Input value={f.q} onChange={(e) => setFaq(i, 'q', e.target.value)} placeholder="Question" />
                <Textarea rows={2} value={f.a} onChange={(e) => setFaq(i, 'a', e.target.value)} placeholder="Answer" />
                <Button variant="ghost" size="sm" onClick={() => removeFaq(i)} className="text-destructive"><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</Button>
              </div>
            ))}
            <Button variant="outline" onClick={addFaq} className="rounded-lg"><Plus className="h-4 w-4 mr-1.5" />Add FAQ</Button>
          </Card>
        </TabsContent>

        <TabsContent value="tags" className="mt-4">
          <Card className="p-6 rounded-2xl border-border space-y-3">
            <p className="text-sm text-muted-foreground">Temperament tags shown on the public site and in the animal editor.</p>
            {(s.tags || []).map((t, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`tag-${i}`} className="flex gap-2 items-center">
                <Input value={t} onChange={(e) => setTag(i, e.target.value)} />
                <Button variant="ghost" size="icon" onClick={() => removeTag(i)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" onClick={addTag} className="rounded-lg"><Plus className="h-4 w-4 mr-1.5" />Add tag</Button>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="mt-4">
          <Card className="p-6 rounded-2xl border-border space-y-4">
            <div className="grid gap-1.5">
              <Label>Contact email (Stable Office notifications)</Label>
              <Input value={s.contact_email || ''} onChange={(e) => update('contact_email', e.target.value)} type="email" />
              <p className="text-xs text-muted-foreground">Real email notifications are disabled in Phase 1 — enquiries appear in the dashboard immediately.</p>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
